import React, { FC, useContext, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { ComponentConsumerProps,ComponentRendering, isEditorActive, Placeholder, SitecoreContextReactContext, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { documentService, windowService } from '../../utils/window.utils';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import useMediaQuery from '../../hooks/useMediaQuery';
import CarouselArrow from './components/carouselArrow';
import CarouselDots from '../@shared/carouselDots';
import CarouselSlide from './components/carouselSlide';
import { ContentHubSpecialOffersModel, ContentHubSpecialOffersSlideModel } from './models/fields.model';
import { ContentHubSpecialOffersProps } from './models/fields.definitions';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import Heading from '../@shared/heading';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AppendDots from './components/appendDots';
import '../../styles/@shared/special-offers.scss';
import './styles.scss';
import { useTranslation } from 'react-i18next';
import useSitecoreContext from '../../hooks/useSitecoreContext';
import useShowProductComponentTitle from '../../hooks/useShowProductComponentTitle';
import { specialOffers } from '../../constants/productspecialoffers.constants';

const ProductSpecialOffers: FC<ComponentConsumerProps & ContentHubSpecialOffersProps> = (props) => {
  const { sitecoreContext } = useSitecoreContext();
  const specialOfferMainPageLink: string = sitecoreContext?.specialOfferPageUrl || '';
  const lineFromPageContext: string = sitecoreContext.analytics?.productLine || '';
  const lineFromParams = props.params?.lineId || '';
  const lineId =
    props.params?.enableProductParameters === '1' ? lineFromParams : lineFromPageContext;
  const { t } = useTranslation();
  const apiResponse = isEmptyResponse(props) && props?.fields?.apiData?.items && props?.fields?.apiData?.items?.length > 0;
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [screenWidth, setScreenWidth] = useState(1680);
  const model = new ContentHubSpecialOffersModel(props, t, specialOfferMainPageLink);
  const hasHeaderValue = !!(model.title?.value || model.eyebrow?.value || model.description?.value);
  const dictionaryTerms = [(props?.titleKeySpeicalOffers ?? ''), (props?.eyebrowKeySpeicalOffers ?? ''), (props?.descriptionKeySpeicalOffers ?? '')];
  const showHeader = useShowProductComponentTitle(hasHeaderValue, dictionaryTerms);

  const cardsCount = model?.items?.length;
  const [activeCardIndex, updateActiveIndex] = useState(0);

  useEffect(() => {
    setScreenWidth(Math.min(windowService()?.innerWidth, 1680));
    if (!isDesktop) {
      const width = 343 / (2 * model?.items?.length);
      documentService()?.querySelector?.('body').style.setProperty('--slick-width', `${width}px`);
      documentService()
        .querySelector('body')
        .style.setProperty('--slick-width-active', `${width + 10}px`);
    }
  }, [model]); // eslint-disable-line

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }
  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const containerPaddingLeft = 32;
  const largeImageWidth = isDesktop ? 664 : 300;
  const smallImageWidth = isDesktop ? 392 : 300;
  const cardMarginRight = 16;

  const totalElementsWidth =
    model.items?.reduce((sumWidths: number, curr: ContentHubSpecialOffersSlideModel) => {
      if (curr.cardStyles.value === 'Wide') {
        sumWidths += largeImageWidth;
      } else {
        sumWidths += smallImageWidth;
      }
      return sumWidths + cardMarginRight;
    }, 0) + containerPaddingLeft;
  const shouldInstanceCarousel = totalElementsWidth > screenWidth;
  const iconImage = isDesktop ? 'arrow-chevron-right' : 'arrow-simple-right';
  const settings = {
    infinite: !isEEActive && shouldInstanceCarousel,
    dots: shouldInstanceCarousel,
    arrows: !isEEActive && shouldInstanceCarousel,
    swipe: shouldInstanceCarousel,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: <CarouselArrow direction="next" />,
    prevArrow: <CarouselArrow direction="previous" />,
    customPaging: CarouselDots,
    appendDots: (dots) => AppendDots({ cardsCount, activeCardIndex, dots }),
  };

  const afterChange = (currentSlide): void => {
    updateActiveIndex(currentSlide);
  };

  return (
    <>
      {model.items.length > 0 ? (
        <div className="product-special-offers special-offers-wrapper" id={specialOffers}>
          <div className="special-offers">
            {showHeader && (
              <div className="special-offers__bgWrapper">
                <Picture
                  productImageProps={{
                    desktopRendition: model.desktopBgImage,
                    mobileRendition: model.mobileBgImage,
                    pictureClassName: 'special-offers__background',
                  }}
                />

                <Heading
                  editable={false}
                  title={model.title}
                  eyebrow={model.eyebrow}
                  subtitle={model.description}
                  isGenericPopup={!!props.rendering?.placeholders?.['jss-generic-popup']?.length}
                  useDictionaryFallback
                  eyebrowDictionaryKey={props?.eyebrowKeySpeicalOffers}
                  titleDictionaryKey={props?.titleKeySpeicalOffers}
                  descriptionDictionaryKey={props?.descriptionKeySpeicalOffers}
                  link={model.getLink(lineId)}
                >
                  <div className={`special-offers__pop-up-cta ${isEEActive ? 'generic-popupEE-width' : ''}`}>
                    <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
                  </div>
                </Heading>
              </div>
            )}
            <Slider {...settings} className="special-offers__cards" afterChange={afterChange}>
              {model.items.map((item) => (
                <div key={item?.id} className="special-offers__slide">
                  <CarouselSlide isSeries={!!props?.isSeries} model={item} isEE={isEEActive || false} iconImage={iconImage} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default withSitecoreContext()(ProductSpecialOffers);
